import { type WorldTransform } from '../entities/WorldTransform';
import { rotatedX, rotatedY } from '../math/MathUtils';
import { type Vector2 } from '../math/Vector2';

/**
 * A placed, sized quad in world space: where a sprite ends up once its transform is folded in.
 *
 * Shared by the two things that need that answer, drawing and hit-testing, so a sprite is never drawn
 * in one place and clicked in another. Fields are mutable because callers refill one instance rather
 * than allocating per sprite per frame.
 */
export interface SpriteQuad {
  /** World X of the anchor. */
  x: number;
  /** World Y of the anchor. */
  y: number;
  /** World rotation in radians, counter-clockwise. */
  rotation: number;
  /** Final width in world units, with the entity's world scale already applied. */
  width: number;
  /** Final height in world units, with the entity's world scale already applied. */
  height: number;
  /** Horizontal pivot as a fraction of width, where 0 is the left edge and 1 the right. */
  anchorX: number;
  /** Vertical pivot as a fraction of height, where 0 is the bottom edge and 1 the top. */
  anchorY: number;
}

/** The two sprite fields that turn a transform into a quad. `SpriteRenderer` satisfies this. */
export interface SpritePlacement {
  /** Size in world units, before the entity's world scale. */
  readonly size: Vector2;
  /** Pivot as a fraction of the size. */
  readonly anchor: Vector2;
}

/** Fills `out` with the world quad a sprite occupies, and returns it. */
export function fillSpriteQuad(out: SpriteQuad, placement: SpritePlacement, world: WorldTransform): SpriteQuad {
  out.x = world.position.x;
  out.y = world.position.y;
  out.rotation = world.rotation;
  out.width = placement.size.x * world.scale.x;
  out.height = placement.size.y * world.scale.y;
  out.anchorX = placement.anchor.x;
  out.anchorY = placement.anchor.y;
  return out;
}

/**
 * Reports whether a world point lands on the quad, rotation included.
 *
 * The point is rotated into the quad's own space rather than the quad being approximated by an
 * axis-aligned box, so a tilted car is clickable exactly where it is drawn.
 *
 * `padding` grows the hit area on every side in world units. It exists because a target sized for a
 * toddler's finger is bigger than the same target sized for the art.
 */
export function spriteQuadContainsPoint(quad: SpriteQuad, pointX: number, pointY: number, padding = 0): boolean {
  const offsetX = pointX - quad.x;
  const offsetY = pointY - quad.y;
  const cos = Math.cos(-quad.rotation);
  const sin = Math.sin(-quad.rotation);

  const localX = rotatedX(offsetX, offsetY, cos, sin);
  const localY = rotatedY(offsetX, offsetY, cos, sin);

  return (
    withinExtent(localX, quad.anchorX, quad.width, padding) && withinExtent(localY, quad.anchorY, quad.height, padding)
  );
}

/*
 * Both edges are derived then ordered, so a sprite mirrored by a negative scale still tests correctly
 * instead of producing an empty range.
 */
function withinExtent(local: number, anchor: number, extent: number, padding: number): boolean {
  const start = -anchor * extent;
  const end = (1 - anchor) * extent;
  return local >= Math.min(start, end) - padding && local <= Math.max(start, end) + padding;
}
